import type { AuthSessionRole, EnvironmentId, ServerConfig } from "@cmmd-forge/contracts";
import { createKnownEnvironment } from "@cmmd-forge/client-runtime";

import { fetchRemoteSessionState, resolveRemoteWebSocketConnectionUrl } from "../remote/api";
import { WsTransport } from "../../rpc/wsTransport";
import { createWsRpcClient, type WsRpcClient } from "../../rpc/wsRpcClient";
import { isCloudBearerAuthFailure } from "../../lib/cloudAuthFailure";
import { isRuntimeTicketAuthFailureMessage } from "./runtimeTicketAuthFailure";
import { createEnvironmentConnection, type EnvironmentConnection } from "./connection";
import {
  getSavedEnvironmentRecord,
  readSavedEnvironmentBearerToken,
  type SavedEnvironmentRecord,
  useSavedEnvironmentRegistryStore,
} from "./catalog";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";
import {
  acquireRuntimeDescriptor,
  unregisterCloudRuntimeCredentialProvider,
  type SavedEnvironmentConnectionTarget,
  type SavedEnvironmentConnectionTargetProvider,
} from "./cloudRuntimeCredentialAccess";
import {
  createCmmdRuntimeTargetProvider,
  createStaticSavedEnvironmentTargetProvider,
  resolveCmmdRuntimeId,
} from "./savedEnvironmentTargetProvider";
import { createCloudRuntimeAuthorityRenewal } from "./cloudRuntimeAuthorityRenewal";
import type { CloudRuntimeCredentialProvider } from "./cloudRuntimeCredentialProvider";
import * as savedCloud from "./savedCloudEnvironmentLifecycle";
import type { SavedEnvironmentAcquisitionRegistryExpectation } from "./savedEnvironmentConnectionRegistry";
import type { createEnvironmentConnectionHandlers } from "./environmentConnectionHandlers";

export type SavedEnvironmentConnectionOptions = {
  readonly client?: WsRpcClient;
  readonly bearerToken?: string;
  readonly role?: AuthSessionRole | null;
  readonly serverConfig?: ServerConfig | null;
  readonly signal?: AbortSignal;
  readonly registryExpectation?: SavedEnvironmentAcquisitionRegistryExpectation;
};

export type SavedEnvironmentConnectionDependencies = {
  readonly findConnection: (environmentId: EnvironmentId) => EnvironmentConnection | undefined;
  readonly registerConnection: (connection: EnvironmentConnection) => EnvironmentConnection;
  readonly canCommit: (input: {
    readonly record: SavedEnvironmentRecord;
    readonly generation: number;
    readonly registryExpectation: SavedEnvironmentAcquisitionRegistryExpectation;
  }) => boolean;
  readonly createHandlers: typeof createEnvironmentConnectionHandlers;
  readonly withMetadataTimeout: <T>(
    record: SavedEnvironmentRecord,
    operation: Promise<T>,
  ) => Promise<T>;
  readonly setConnecting: (environmentId: EnvironmentId) => void;
  readonly setConnected: (environmentId: EnvironmentId) => void;
  readonly setDisconnected: (environmentId: EnvironmentId, reason?: string | null) => void;
  readonly setError: (environmentId: EnvironmentId, error: unknown) => void;
};

function isoNow(): string {
  return new Date().toISOString();
}

function createSavedEnvironmentClient(
  record: SavedEnvironmentRecord,
  targetProvider: SavedEnvironmentConnectionTargetProvider,
  canCommit: () => boolean,
  usesCmmdRuntimeProvider: boolean,
  dependencies: SavedEnvironmentConnectionDependencies,
): WsRpcClient {
  if (canCommit()) useSavedEnvironmentRuntimeStore.getState().ensure(record.environmentId);
  return createWsRpcClient(
    new WsTransport(
      async () => {
        const resolveUrl = (target: SavedEnvironmentConnectionTarget) =>
          resolveRemoteWebSocketConnectionUrl({
            wsBaseUrl: target.wsBaseUrl,
            httpBaseUrl: target.httpBaseUrl,
            bearerToken: target.bearerToken,
            ...(usesCmmdRuntimeProvider ? { runtimeTicket: target.bearerToken } : {}),
          });
        const target = await targetProvider.acquire();
        try {
          return await resolveUrl(target);
        } catch (error) {
          if (!usesCmmdRuntimeProvider || !isCloudBearerAuthFailure(error)) throw error;
          return resolveUrl(await targetProvider.acquire({ forceRefresh: true }));
        }
      },
      {
        trackGlobalConnection: false,
        onAttempt: () => {
          if (canCommit()) dependencies.setConnecting(record.environmentId);
        },
        onOpen: () => {
          if (canCommit()) dependencies.setConnected(record.environmentId);
        },
        onError: (message: string) => {
          if (usesCmmdRuntimeProvider && isRuntimeTicketAuthFailureMessage(message)) {
            targetProvider.invalidate?.();
          }
          if (canCommit()) {
            useSavedEnvironmentRuntimeStore.getState().patch(record.environmentId, {
              connectionState: "error",
              lastError: message,
              lastErrorAt: isoNow(),
            });
          }
        },
        onClose: (details: { readonly code: number; readonly reason: string }) => {
          if (
            usesCmmdRuntimeProvider &&
            (details.code === 1008 || isRuntimeTicketAuthFailureMessage(details.reason))
          ) {
            targetProvider.invalidate?.();
          }
          if (canCommit()) dependencies.setDisconnected(record.environmentId, details.reason);
        },
      },
    ),
  );
}

async function refreshSavedEnvironmentMetadata(
  record: SavedEnvironmentRecord,
  targetProvider: SavedEnvironmentConnectionTargetProvider,
  client: WsRpcClient,
  canCommit: () => boolean,
  roleHint?: AuthSessionRole | null,
  configHint?: ServerConfig | null,
  signal?: AbortSignal,
): Promise<boolean> {
  const target = await acquireRuntimeDescriptor(targetProvider, signal);
  const [serverConfig, sessionState] = await Promise.all([
    configHint ? Promise.resolve(configHint) : client.server.getConfig(),
    fetchRemoteSessionState({
      httpBaseUrl: target.httpBaseUrl,
      bearerToken: target.bearerToken,
      ...(signal ? { signal } : {}),
    }),
  ]);
  if (!canCommit()) return false;
  useSavedEnvironmentRuntimeStore.getState().patch(record.environmentId, {
    authState: sessionState.authenticated ? "authenticated" : "requires-auth",
    descriptor: serverConfig.environment,
    serverConfig,
    role: sessionState.authenticated ? (sessionState.role ?? roleHint ?? null) : null,
  });
  persistAuthenticatedPullRequestCapability(
    record,
    serverConfig,
    sessionState.authenticated,
    canCommit,
  );
  return true;
}

function persistAuthenticatedPullRequestCapability(
  record: SavedEnvironmentRecord,
  serverConfig: ServerConfig,
  authenticated: boolean,
  canCommit: () => boolean,
): void {
  if (!authenticated || !canCommit()) return;
  const persistedRecord = getSavedEnvironmentRecord(record.environmentId);
  if (!persistedRecord) return;
  useSavedEnvironmentRegistryStore.getState().upsert({
    ...persistedRecord,
    lastAuthenticatedPullRequestCapability:
      serverConfig.environment.capabilities?.pullRequests === true,
  });
}

export async function createSavedEnvironmentConnection(
  record: SavedEnvironmentRecord,
  generation: number,
  dependencies: SavedEnvironmentConnectionDependencies,
  options?: SavedEnvironmentConnectionOptions,
): Promise<EnvironmentConnection> {
  const existing = dependencies.findConnection(record.environmentId);
  if (existing) return existing;
  const registryExpectation = options?.registryExpectation ?? { kind: "existing-record", record };
  const canCommit = () => dependencies.canCommit({ record, generation, registryExpectation });
  const { isCmmdRuntime, targetProvider, initialTarget } = await resolveSavedConnectionTarget(
    record,
    options,
    canCommit,
  );
  if (!canCommit()) {
    unregisterRuntimeTargetProvider(record.environmentId, targetProvider, isCmmdRuntime);
    throw new Error("Saved environment changed while its connection was being acquired.");
  }
  const client =
    options?.client ??
    createSavedEnvironmentClient(record, targetProvider, canCommit, isCmmdRuntime, dependencies);
  const connection = createConfiguredSavedEnvironmentConnection({
    record,
    initialTarget,
    targetProvider,
    isCmmdRuntime,
    client,
    canCommit,
    dependencies,
  });
  try {
    const metadataWasCommitted = await dependencies.withMetadataTimeout(
      record,
      refreshSavedEnvironmentMetadata(
        record,
        targetProvider,
        client,
        canCommit,
        options?.role ?? null,
        options?.serverConfig ?? null,
        options?.signal,
      ),
    );
    if (!metadataWasCommitted || !canCommit()) {
      throw new Error("Saved environment changed while its connection was being acquired.");
    }
    return dependencies.registerConnection(connection);
  } catch (error) {
    if (isCmmdRuntime) {
      unregisterCloudRuntimeCredentialProvider(
        record.environmentId,
        targetProvider as CloudRuntimeCredentialProvider,
      );
    }
    if (canCommit()) dependencies.setError(record.environmentId, error);
    await connection.dispose().catch(() => undefined);
    throw error;
  }
}

async function resolveSavedConnectionTarget(
  record: SavedEnvironmentRecord,
  options: SavedEnvironmentConnectionOptions | undefined,
  canCommit: () => boolean,
) {
  const isCmmdCloud = savedCloud.isCmmdCloudEnvironment(record);
  const resolvedRuntimeId = isCmmdCloud ? resolveCmmdRuntimeId(record) : null;
  const isCmmdRuntime = resolvedRuntimeId !== null;
  const bearerToken = await readConnectionBearerToken(record, options, isCmmdRuntime, canCommit);
  const targetProvider: SavedEnvironmentConnectionTargetProvider = isCmmdRuntime
    ? createCmmdRuntimeTargetProvider(
        record,
        resolvedRuntimeId,
        bearerToken ?? undefined,
        canCommit,
      )
    : createStaticSavedEnvironmentTargetProvider({ record, bearerToken: bearerToken! });
  try {
    const initialTarget = await acquireRuntimeDescriptor(targetProvider, options?.signal);
    return { isCmmdRuntime, targetProvider, initialTarget };
  } catch (error) {
    unregisterRuntimeTargetProvider(record.environmentId, targetProvider, isCmmdRuntime);
    throw error;
  }
}

function unregisterRuntimeTargetProvider(
  environmentId: EnvironmentId,
  targetProvider: SavedEnvironmentConnectionTargetProvider,
  isCmmdRuntime: boolean,
): void {
  if (!isCmmdRuntime) return;
  unregisterCloudRuntimeCredentialProvider(
    environmentId,
    targetProvider as CloudRuntimeCredentialProvider,
  );
}

async function readConnectionBearerToken(
  record: SavedEnvironmentRecord,
  options: SavedEnvironmentConnectionOptions | undefined,
  isCmmdRuntime: boolean,
  canCommit: () => boolean,
): Promise<string | undefined> {
  if (isCmmdRuntime) return options?.bearerToken;
  const bearerToken =
    options?.bearerToken ?? (await readSavedEnvironmentBearerToken(record.environmentId));
  if (bearerToken) return bearerToken;
  if (canCommit()) {
    useSavedEnvironmentRuntimeStore.getState().patch(record.environmentId, {
      authState: "requires-auth",
      role: null,
      connectionState: "disconnected",
      lastError: "Saved environment is missing its saved credential. Pair it again.",
      lastErrorAt: isoNow(),
    });
  }
  throw new Error("Saved environment is missing its saved credential.");
}

function createConfiguredSavedEnvironmentConnection(input: {
  readonly record: SavedEnvironmentRecord;
  readonly initialTarget: SavedEnvironmentConnectionTarget;
  readonly targetProvider: SavedEnvironmentConnectionTargetProvider;
  readonly isCmmdRuntime: boolean;
  readonly client: WsRpcClient;
  readonly canCommit: () => boolean;
  readonly dependencies: SavedEnvironmentConnectionDependencies;
}): EnvironmentConnection {
  const { record, initialTarget, targetProvider, isCmmdRuntime, client, canCommit, dependencies } =
    input;
  const knownEnvironment = createKnownEnvironment({
    id: record.environmentId,
    label: record.label,
    source: "manual",
    target: { httpBaseUrl: initialTarget.httpBaseUrl, wsBaseUrl: initialTarget.wsBaseUrl },
  });
  const authorityRenewal =
    isCmmdRuntime && initialTarget.expiresAt
      ? createCloudRuntimeAuthorityRenewal({
          initialDescriptor: { ...initialTarget, expiresAt: initialTarget.expiresAt },
          renew: async () => {
            const renewed = await targetProvider.acquire({ forceRefresh: true });
            return { ...renewed, expiresAt: renewed.expiresAt ?? null };
          },
          onState: (providerAuthority) => {
            if (canCommit()) {
              useSavedEnvironmentRuntimeStore
                .getState()
                .patch(record.environmentId, { providerAuthority });
            }
          },
        })
      : null;
  return createEnvironmentConnection({
    kind: "saved",
    knownEnvironment: { ...knownEnvironment, environmentId: record.environmentId },
    client,
    readiness: { bearerToken: initialTarget.bearerToken },
    tolerateReportedEnvironmentIdentity: isCmmdRuntime,
    refreshMetadata: async () => {
      await refreshSavedEnvironmentMetadata(record, targetProvider, client, canCommit);
    },
    ...(authorityRenewal
      ? {
          ensureProviderAuthority: authorityRenewal.ensureLive,
          onDispose: authorityRenewal.dispose,
        }
      : {}),
    onConfigSnapshot: (config) => {
      if (canCommit()) {
        useSavedEnvironmentRuntimeStore.getState().patch(record.environmentId, {
          descriptor: config.environment,
          serverConfig: config,
        });
      }
    },
    ...dependencies.createHandlers((payload) => {
      if (canCommit()) {
        useSavedEnvironmentRuntimeStore.getState().patch(record.environmentId, {
          descriptor: payload.environment,
        });
      }
    }),
  });
}
